import clsx from 'clsx';
import { forwardRef, type HTMLAttributes, type ReactElement } from 'react';

import type { DataTestId } from '../../../core/types/data-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import type { WithChildren } from '../../../core/types/with-children.js';
import { DATA_TABLE_DEFAULT_HORIZONTAL_ALIGNMENT } from '../constants.js';
import * as alignmentStyles from './Alignment.sty.js';
import * as cellStyles from './DataTableCell.sty.js';
import * as defaultCellStyles from './DataTableDefaultCell.sty.js';
import {
  useDataTable,
  useDataTableColumn,
  useDataTableIsHeaderContextProvider,
} from '../contexts/DataTableContext.js';
import {
  SubRowsCellExpander,
  SubRowsHeaderExpander,
} from '../features/SubRows/SubRowExpanders.js';

/**
 * Props for the DataTable.DefaultCell
 * @public
 */
export type DataTableDefaultCellProps = WithChildren &
  StylingProps &
  DataTestId &
  HTMLAttributes<HTMLDivElement>;

/**
 * DataTable custom Cell renderer wrapper. This component automatically deals
 * with applying the correct cell styles.
 * @public
 */
export const DataTableDefaultCell = forwardRef<
  HTMLDivElement,
  DataTableDefaultCellProps
>((props, forwardedRef): ReactElement | null => {
  const {
    children,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    ...remainingProps
  } = props;

  const table = useDataTable();
  const column = useDataTableColumn();
  const isHeader = useDataTableIsHeaderContextProvider();

  return (
    <div
      ref={forwardedRef}
      className={clsx(
        defaultCellStyles.defaultCell({
          rowDensity: table.getVisualVariantOptions().rowDensity,
        }),
        alignmentStyles.horizontalAlignment[
          column.columnDef.alignment ?? DATA_TABLE_DEFAULT_HORIZONTAL_ALIGNMENT
        ],
        consumerClassName,
      )}
      style={consumerStyle}
      data-testid={dataTestId}
      {...remainingProps}
    >
      {table.options.enableExpanding === true &&
        table.getSubRowIndicatorColumnId() === column.columnDef.id &&
        (isHeader ? (
          <SubRowsHeaderExpander
            isAllRowsExpanded={table.getIsAllRowsExpanded()}
          />
        ) : (
          <SubRowsCellExpander columnType={column.columnDef.columnType} />
        ))}
      {!column.columnDef.meta?.builtin ? (
        <div
          className={clsx(
            cellStyles.innerCell({
              lineWrap: !isHeader && column.getIsLineWrapped(),
            }),
          )}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
});

(
  DataTableDefaultCell as typeof DataTableDefaultCell & {
    displayName: string;
  }
).displayName = 'DataTable.DefaultCell';
